import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { vortxTypography } from '@vortx/shared-ui';

const RetroWireframeGlobe = ({ size = 120, color = '#b28c50' }) => {
    const [isHovering, setIsHovering] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [rotation, setRotation] = useState({ x: 15, y: 0 });
    const [velocity, setVelocity] = useState({ x: 0, y: 18 }); // degrees per second
    const [clickPulse, setClickPulse] = useState(false);
    const lastMousePos = useRef({ x: 0, y: 0 });
    const lastTime = useRef(Date.now());
    const containerRef = useRef(null);

    // Auto-rotation when not dragging
    useEffect(() => {
        if (isDragging) return;

        const animate = () => {
            const now = Date.now();
            const delta = (now - lastTime.current) / 1000;
            lastTime.current = now;

            setRotation(prev => ({
                x: prev.x + velocity.x * delta,
                y: prev.y + velocity.y * delta,
            }));

            // Gradually return to default speed when not hovering
            if (!isHovering && !isDragging) {
                setVelocity(prev => ({
                    x: prev.x * 0.95,
                    y: prev.y + (18 - prev.y) * 0.02,
                }));
            }
        };

        const interval = setInterval(animate, 16);
        return () => clearInterval(interval);
    }, [isDragging, isHovering, velocity]);

    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        setIsDragging(true);
        lastMousePos.current = { x: e.clientX, y: e.clientY };
        lastTime.current = Date.now();
    }, []);

    const handleMouseMove = useCallback((e) => {
        if (!isDragging) return;

        const deltaX = e.clientX - lastMousePos.current.x;
        const deltaY = e.clientY - lastMousePos.current.y;
        const deltaTime = (Date.now() - lastTime.current) / 1000;

        // Update rotation
        setRotation(prev => ({
            x: prev.x - deltaY * 0.5,
            y: prev.y + deltaX * 0.5,
        }));

        // Calculate velocity for momentum
        if (deltaTime > 0) {
            setVelocity({
                x: -deltaY * 0.5 / deltaTime * 0.1,
                y: deltaX * 0.5 / deltaTime * 0.1,
            });
        }

        lastMousePos.current = { x: e.clientX, y: e.clientY };
        lastTime.current = Date.now();
    }, [isDragging]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Handle click pulse
    const handleClick = useCallback(() => {
        if (isDragging) return;
        setClickPulse(true);
        // Boost rotation speed temporarily
        setVelocity(prev => ({
            x: prev.x,
            y: prev.y + 50,
        }));
        setTimeout(() => setClickPulse(false), 500);
    }, [isDragging]);

    // Global mouse events for dragging
    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    const activeColor = isHovering || isDragging ? '#5fd4c4' : color;
    const glowIntensity = clickPulse ? 20 : (isHovering ? 12 : 8);

    return (
        <Box
            ref={containerRef}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            onMouseDown={handleMouseDown}
            onClick={handleClick}
            sx={{
                width: size,
                height: size,
                position: 'relative',
                margin: '0 auto',
                perspective: '500px',
                transformStyle: 'preserve-3d',
                cursor: isDragging ? 'grabbing' : 'grab',
                userSelect: 'none',
            }}
        >
            {/* Rotating container */}
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    transformStyle: 'preserve-3d',
                    transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`,
                    transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                }}
            >
                {/* Horizontal rings (latitude lines) */}
                {[0, 30, 60, 90, 120, 150].map((angle, i) => (
                    <Box
                        key={`h-${i}`}
                        sx={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            border: `1px solid ${activeColor}`,
                            borderRadius: '50%',
                            transform: `rotateX(${angle}deg)`,
                            opacity: isHovering ? 0.8 : 0.6,
                            boxShadow: `0 0 ${glowIntensity}px ${activeColor}`,
                            transition: 'all 0.3s ease',
                        }}
                    />
                ))}

                {/* Vertical rings (longitude lines) */}
                {[0, 45, 90, 135].map((angle, i) => (
                    <Box
                        key={`v-${i}`}
                        sx={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            border: `1px solid ${activeColor}`,
                            borderRadius: '50%',
                            transform: `rotateY(${angle}deg)`,
                            opacity: isHovering ? 0.8 : 0.6,
                            boxShadow: `0 0 ${glowIntensity}px ${activeColor}`,
                            transition: 'all 0.3s ease',
                        }}
                    />
                ))}

                {/* Center glow point */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: clickPulse ? 12 : 8,
                        height: clickPulse ? 12 : 8,
                        borderRadius: '50%',
                        background: activeColor,
                        transform: 'translate(-50%, -50%)',
                        boxShadow: `0 0 ${clickPulse ? 30 : 15}px ${activeColor}, 0 0 ${clickPulse ? 50 : 30}px ${activeColor}`,
                        transition: 'all 0.2s ease',
                    }}
                />
            </Box>

            {/* Click pulse ring */}
            {clickPulse && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: size,
                        height: size,
                        borderRadius: '50%',
                        border: `2px solid ${activeColor}`,
                        transform: 'translate(-50%, -50%)',
                        animation: 'pulseRing 0.5s ease-out forwards',
                        '@keyframes pulseRing': {
                            '0%': { transform: 'translate(-50%, -50%) scale(0.5)', opacity: 1 },
                            '100%': { transform: 'translate(-50%, -50%) scale(1.3)', opacity: 0 },
                        },
                    }}
                />
            )}

            {/* Outer glow ring */}
            <Box
                sx={{
                    position: 'absolute',
                    top: -4,
                    left: -4,
                    right: -4,
                    bottom: -4,
                    borderRadius: '50%',
                    border: `1px solid ${activeColor}`,
                    opacity: isHovering ? 0.4 : 0.2,
                    transform: clickPulse ? 'scale(1.1)' : 'scale(1)',
                    transition: 'all 0.3s ease',
                }}
            />

            {/* Interaction hint */}
            {isHovering && !isDragging && (
                <Typography
                    sx={{
                        position: 'absolute',
                        bottom: -20,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.45rem',
                        color: '#666',
                        whiteSpace: 'nowrap',
                    }}
                >
                    drag to rotate
                </Typography>
            )}

            {/* Speed indicator */}
            {(isHovering || Math.abs(velocity.y) > 25) && (
                <Typography
                    sx={{
                        position: 'absolute',
                        top: -16,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.5rem',
                        color: Math.abs(velocity.y) > 30 ? '#5fd4c4' : '#666',
                        textShadow: Math.abs(velocity.y) > 30 ? '0 0 4px rgba(95, 212, 196, 0.5)' : 'none',
                    }}
                >
                    {Math.abs(velocity.y).toFixed(0)}°/s
                </Typography>
            )}
        </Box>
    );
};

export default RetroWireframeGlobe;
